export interface MarketCategory {
  slug: string;
  label: string;
  emoji: string;
}

export const MARKET_CATEGORIES: readonly MarketCategory[] = [
  { slug: 'baby_clothes', label: 'Детская одежда', emoji: '👶' },
  { slug: 'mom_clothes', label: 'Одежда для мамы', emoji: '👗' },
  { slug: 'strollers', label: 'Коляски и кресла', emoji: '🛻' },
  { slug: 'toys', label: 'Игрушки', emoji: '🧸' },
  { slug: 'furniture', label: 'Мебель', emoji: '🪑' },
  { slug: 'books', label: 'Книги', emoji: '📚' },
  { slug: 'feeding', label: 'Питание и уход', emoji: '🍼' },
  { slug: 'electronics', label: 'Электроника', emoji: '📱' },
  { slug: 'services', label: 'Услуги', emoji: '🤝' },
  { slug: 'other', label: 'Другое', emoji: '📦' },
] as const;

export type MarketCategorySlug = typeof MARKET_CATEGORIES[number]['slug'];

export const categoryLabel = (slug: string | null | undefined): string => {
  if (!slug) return '';
  return MARKET_CATEGORIES.find((c) => c.slug === slug)?.label ?? slug;
};

// Legacy Cyrillic labels that may appear in marketplace_products (pre-2026-04-23 rows)
// or in listings.category (Telegram bot writes whatever comes from public channels).
// Mapping is normalize-at-read only — DB rows are NOT rewritten.
export const CATEGORY_LABEL_TO_SLUG: Record<string, string> = {
  // canonical labels from MARKET_CATEGORIES
  'Детская одежда': 'baby_clothes',
  'Одежда для мамы': 'mom_clothes',
  'Коляски и кресла': 'strollers',
  'Игрушки': 'toys',
  'Мебель': 'furniture',
  'Книги': 'books',
  'Питание и уход': 'feeding',
  'Электроника': 'electronics',
  'Услуги': 'services',
  'Другое': 'other',
  // historical / telegram-sourced variants
  'Коляски и автокресла': 'strollers',
  'Коляски': 'strollers',
  'Автокресла': 'strollers',
  'Одежда': 'baby_clothes',
  'Одежда детская': 'baby_clothes',
  'Одежда для детей': 'baby_clothes',
  'Детские вещи': 'baby_clothes',
  'Одежда мамы': 'mom_clothes',
  'Книги и развитие': 'books',
  'Развитие': 'books',
  'Игры и игрушки': 'toys',
  'Питание': 'feeding',
  'Уход': 'feeding',
  'Кормление': 'feeding',
  'Мебель и интерьер': 'furniture',
  'Техника': 'electronics',
  'Гаджеты': 'electronics',
  'Услуги и помощь': 'services',
  'Прочее': 'other',
};

const KNOWN_SLUGS: ReadonlySet<string> = new Set(MARKET_CATEGORIES.map((c) => c.slug));
const warnedUnknownCategories = new Set<string>();

export function normalizeCategoryToSlug(raw: string | null | undefined): string | null {
  if (raw === null || raw === undefined) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;
  if (KNOWN_SLUGS.has(trimmed)) return trimmed;
  const mapped = CATEGORY_LABEL_TO_SLUG[trimmed];
  if (mapped) return mapped;
  if (!warnedUnknownCategories.has(trimmed)) {
    warnedUnknownCategories.add(trimmed);
    console.warn(`[marketCategories] unknown category value: "${trimmed}" — passing through`);
  }
  return trimmed;
}
